export type TaxonomyNode = {
  id: string;
  name: string;
  path: string;
  parentId: string | null;
  isLeaf: boolean;
  level: number;
};

type EbayTreeNode = {
  category?: { categoryId?: string; categoryName?: string };
  leafCategoryTreeNode?: boolean;
  categoryTreeNodeLevel?: number;
  childCategoryTreeNodes?: EbayTreeNode[];
};

type BcCategoryRow = {
  id?: number;
  parent_id?: number | null;
  name?: string;
};

export function flattenEbayCategoryTree(root: EbayTreeNode | null | undefined, treeVersion?: string): TaxonomyNode[] {
  void treeVersion;
  const out: TaxonomyNode[] = [];
  const walk = (node: EbayTreeNode, parentId: string | null, parentPath: string) => {
    const id = String(node.category?.categoryId ?? "").trim();
    const name = String(node.category?.categoryName ?? "").trim();
    if (!id || !name) return;
    const path = parentPath ? `${parentPath} > ${name}` : name;
    const children = node.childCategoryTreeNodes ?? [];
    out.push({
      id,
      name,
      path,
      parentId,
      isLeaf: Boolean(node.leafCategoryTreeNode) || children.length === 0,
      level: typeof node.categoryTreeNodeLevel === "number" ? node.categoryTreeNodeLevel : parentPath ? parentPath.split(" > ").length : 0,
    });
    for (const child of children) walk(child, id, path);
  };
  if (root) walk(root, null, "");
  return out;
}

export function flattenBigCommerceCategories(rows: BcCategoryRow[]): TaxonomyNode[] {
  const byId = new Map<string, { id: string; name: string; parentId: string | null }>();
  for (const row of rows) {
    if (row.id == null || !row.name) continue;
    const id = String(row.id);
    const parentId = row.parent_id == null || row.parent_id === 0 ? null : String(row.parent_id);
    byId.set(id, { id, name: row.name, parentId });
  }
  const pathMemo = new Map<string, string>();
  const pathOf = (id: string, stack: string[] = []): string => {
    if (pathMemo.has(id)) return pathMemo.get(id)!;
    const row = byId.get(id);
    if (!row) return id;
    if (stack.includes(id)) return row.name;
    const parentPath = row.parentId ? pathOf(row.parentId, [...stack, id]) : "";
    const path = parentPath ? `${parentPath} > ${row.name}` : row.name;
    pathMemo.set(id, path);
    return path;
  };
  return [...byId.values()].map((row) => ({
    id: row.id,
    name: row.name,
    path: pathOf(row.id),
    parentId: row.parentId,
    isLeaf: ![...byId.values()].some((other) => other.parentId === row.id),
    level: pathOf(row.id).split(" > ").length - 1,
  }));
}

export function workspaceColumnClass(channelCount: number): string {
  if (channelCount <= 1) return "grid grid-cols-1 gap-4";
  if (channelCount === 2) return "grid grid-cols-1 md:grid-cols-2 gap-4";
  return "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";
}
